import { ProjectService } from '@/api/project';
import { Loading } from '@/components/Loading';
import { Section } from '@/components/Section';
import { DeleteRelease } from '@/components/release/DeleteRelease';
import { CreateButton } from '@/components/shared/button/CreateButton';
import { OutlineButton } from '@/components/shared/button/OutlineButton';
import { useCustomSpec } from '@/utils/hooks/useCustomSpec';
import React, { useRef } from 'react';
import { FaArchive } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ExtendedTableLowCode,
  ExtendedTableLowCodeRef,
} from '@/components/shared/table/ExtendedTableLowCode';
import { useAccess } from '@/utils/hooks/useAccess';
import { IFilterSpec } from '@/components/shared/filter/TableFilterPanel';
import { CustomCellProperties } from 'alurkerja-ui';
import { LinkButton } from '@/components/shared/button/LinkButton';
import { FaPen } from 'react-icons/fa6';
import { HideCustomCreateButton } from '@/components/shared/button/HideCustomButtonCreate';
import { IReleaseSchema } from '@/api/release';
import { BiSolidShow } from 'react-icons/bi';

interface CustomActionCellProps {
  data: IReleaseSchema;
  isViewer: boolean;
  tableRef: React.RefObject<ExtendedTableLowCodeRef>;
}

const CustomActionCell: React.FC<CustomActionCellProps> = ({
  data,
  isViewer,
  tableRef,
}) => {
  const renderViewerActions = () => (
    <LinkButton path={`${data.id}`} icon={<BiSolidShow size={20} />} />
  );

  const renderEditorActions = () => (
    <>
      <LinkButton path={`${data.id}`} icon={<BiSolidShow size={20} />} />
      <LinkButton path={`${data.id}/edit`} icon={<FaPen size={15} />} />
      <DeleteRelease
        id={data.id!}
        onSuccess={() => tableRef.current?.refetch()}
      />
    </>
  );

  return isViewer ? renderViewerActions() : renderEditorActions();
};

export const ListRelease: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const ref = useRef<ExtendedTableLowCodeRef>(null);

  const isViewer = useAccess(id!);

  const spec = useCustomSpec('/api/crud/release', {
    columns: [
      { key: 'name', label: 'Title' },
      { key: 'start_date', label: 'Start Date', type: 'date' },
      { key: 'end_date', label: 'End Date', type: 'date' },
    ],
  });

  const filterSpec: IFilterSpec[] = [
    { name: 'name', label: 'Title', type: 'text' },
    { name: 'start_date', label: 'Start Date', type: 'date' },
    { name: 'end_date', label: 'End Date', type: 'date' },
  ];

  const customCell = ({
    name,
    rowValue,
    defaultCell,
  }: CustomCellProperties) => {
    if (name === 'passed_percentage') {
      return (
        <div className="font-semibold">
          <span>
            {((rowValue.passed_percentage ?? 0) * 100).toFixed(0)}% PASSED
          </span>
        </div>
      );
    }
    return defaultCell;
  };

  const { data: projectData, isFetching: isProjectFetching } =
    ProjectService.useGet(id);

  const toArchive = () => navigate('archive');

  if (isProjectFetching) {
    return (
      <Section>
        <Loading />
      </Section>
    );
  }

  return (
    <Section
      title={`Release - ${projectData?.title}`}
      actions={
        !isViewer ? (
          <div className="flex flex-row gap-3">
            <CreateButton label="Add Release" />
            <OutlineButton icon={<FaArchive />} onClick={toArchive}>
              Archive
            </OutlineButton>
          </div>
        ) : (
          <></>
        )
      }
    >
      <ExtendedTableLowCode
        ref={ref}
        spec={spec}
        defaultFilter={{ project_id: id, is_archive: 'false' }}
        defaultSort="name"
        defaultSortOrder="desc"
        customButtonCreate={HideCustomCreateButton}
        customCell={customCell}
        customActionCell={data => (
          <CustomActionCell
            data={data as IReleaseSchema}
            isViewer={isViewer}
            tableRef={ref}
          />
        )}
        filterSpec={filterSpec}
        showFilter
      />
    </Section>
  );
};
